import { supabase } from 'src/lib/supabaseClient';

/**
 * Update user metadata in Supabase auth
 * @param metadata - The metadata to update
 * @returns - Object with success status and error if any
 */
export const updateUserMetadata = async (metadata: Record<string, any>) => {
  try {
    // Update the current user's metadata
    const { data, error } = await supabase.auth.updateUser({
      data: metadata
    });

    if (error) {
      console.error('Error updating user metadata:', error);
      return { success: false, error };
    }

    console.log('User metadata updated successfully:', metadata);
    return { success: true, data };
  } catch (error) {
    console.error('Error in updateUserMetadata:', error);
    return { success: false, error };
  }
};

/**
 * Middleware function to refresh the auth token
 * This should be called before any authenticated API request
 */
export const refreshToken = async () => {
  try {
    // Get the current session
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      console.log('No session found, cannot refresh token');
      return null;
    }

    // Check if token is expired or will expire soon (within 5 minutes)
    const expiresAt = session.expires_at;
    const isExpiringSoon = expiresAt && (expiresAt * 1000 - 300000) < Date.now();

    if (isExpiringSoon) {
      console.log('Token is expiring soon, refreshing...');

      // Use getUser to validate and refresh the token
      const { data: { user }, error } = await supabase.auth.getUser();

      if (error || !user) {
        console.error('Error refreshing token:', error);
        // Session is invalid, sign out
        await supabase.auth.signOut();
        return null;
      }

      // Get the refreshed session
      const { data: { session: refreshedSession } } = await supabase.auth.getSession();
      return refreshedSession;
    }

    return session;
  } catch (error) {
    console.error('Error in refreshToken middleware:', error);
    return null;
  }
};

/**
 * Utility to check if the user is authenticated with a valid token
 * Returns the user if authenticated, null otherwise
 */
export const getAuthenticatedUser = async () => {
  try {
    // This will validate the token with the Supabase Auth server
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error || !user) {
      console.error('Error getting authenticated user:', error);
      return null;
    }

    return user;
  } catch (error) {
    console.error('Error in getAuthenticatedUser:', error);
    return null;
  }
};